import React, { useEffect, useState } from "react";
import {
  getSettings,
  updateSettings,
  updateReglementHeaderImage,
  updateReglementFirstImage,
  updateReglementSecondImage,
} from "../../../services/settingsServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaSave, FaTimes } from "react-icons/fa";

/* Quill */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const ReglementInterieurForm = () => {
  const [settings, setSettings] = useState({
    reglement_interieur: "",
    reglement_interieur_url_header: "",
    reglement_interieur_url_firstimage: "",
    reglement_interieur_url_secondimage: "",
  });

  const [loading, setLoading] = useState(true);

  // Upload states (séparés pour chaque carte)
  const [isUploading, setIsUploading] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState(null);
  const [selectedFirst, setSelectedFirst] = useState(null);
  const [selectedSecond, setSelectedSecond] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();
        if (Array.isArray(settingsData) && settingsData.length > 0) {
          setSettings((prev) => ({ ...prev, ...settingsData[0] }));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleContentChange = (name, value) => {
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateSettings(settings);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  /** UPLOAD HANDLERS **/
  const handleUploadHeader = async () => {
    if (!selectedHeader) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const imageUrl = await updateReglementHeaderImage(selectedHeader, token);
      if (imageUrl) {
        setSettings((prev) => ({
          ...prev,
          reglement_interieur_url_header: `${imageUrl}?t=${Date.now()}`,
        }));
        toast.success("Image d'en-tête mise à jour !");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedHeader(null);
      setIsUploading(false);
    }
  };

  const handleUploadFirstImage = async () => {
    if (!selectedFirst) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const imageUrl = await updateReglementFirstImage(selectedFirst, token);
      if (imageUrl) {
        setSettings((prev) => ({
          ...prev,
          reglement_interieur_url_firstimage: `${imageUrl}?t=${Date.now()}`,
        }));
        toast.success("Première image mise à jour !");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedFirst(null);
      setIsUploading(false);
    }
  };

  const handleUploadSecondImage = async () => {
    if (!selectedSecond) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const imageUrl = await updateReglementSecondImage(selectedSecond, token);
      if (imageUrl) {
        setSettings((prev) => ({
          ...prev,
          reglement_interieur_url_secondimage: `${imageUrl}?t=${Date.now()}`,
        }));
        toast.success("Deuxième image mise à jour !");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedSecond(null);
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-64 bg-white/25 rounded animate-pulse" />
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-slate-100/80 dark:bg-slate-800/40 animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const fallback = "/img/article_placeholder.jpg";

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
            <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
              Règlement intérieur — Contenu & Images
            </h2>
            <p className="text-white/90 text-sm sm:text-[15px] mt-1">
              Éditez le texte et gérez les visuels associés (en-tête, images de section).
            </p>
          </div>

          {/* CONTENT */}
          <div className="p-4 sm:p-6 space-y-6">
            {/* Contenu (Quill) */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Texte du règlement intérieur
                </label>
                <div
                  className="mt-1
                  [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                  [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                  [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                  [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.reglement_interieur || ""}
                    onChange={(v) => handleContentChange("reglement_interieur", v)}
                    theme="snow"
                    formats={formats}
                    modules={modules}
                    placeholder="<p>Rédigez ici le règlement intérieur…</p>"
                  />
                </div>
                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                  Astuce : utilisez des titres (H1/H2) et listes pour structurer les sections.
                </p>
              </div>

              {/* Cartes d'images */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ImageUploadCard
                  title="Image d’en-tête"
                  hint="Grand visuel bannière recommandé (ex : 1920×600)."
                  currentImage={settings.reglement_interieur_url_header || ""}
                  selectedImage={selectedHeader}
                  onSelect={setSelectedHeader}
                  onSave={handleUploadHeader}
                  isUploading={isUploading}
                  fallback={fallback}
                />
                <ImageUploadCard
                  title="Première image"
                  hint="Visuel illustrant la première section (ex : 1280×720)."
                  currentImage={settings.reglement_interieur_url_firstimage || ""}
                  selectedImage={selectedFirst}
                  onSelect={setSelectedFirst}
                  onSave={handleUploadFirstImage}
                  isUploading={isUploading}
                  fallback={fallback}
                />
                <ImageUploadCard
                  title="Deuxième image"
                  hint="Visuel illustrant la deuxième section (ex : 1280×720)."
                  currentImage={settings.reglement_interieur_url_secondimage || ""}
                  selectedImage={selectedSecond}
                  onSelect={setSelectedSecond}
                  onSave={handleUploadSecondImage}
                  isUploading={isUploading}
                  fallback={fallback}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end">
                <Button text="Mettre à jour" type="submit" icon={FaSave} className="!rounded-xl gap-2" />
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Styles locaux */}
      <style>{`
        /* Placeholders */
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }
        /* Contraste du gras dans le contenu en dark */
        .dark .ql-editor strong, .dark .ql-editor b { color: #fff; }
        /* Apple-friendly */
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

/** Carte réutilisable Upload Image (style Settings) */
const ImageUploadCard = ({
  title,
  hint,
  currentImage,
  selectedImage,
  onSelect,
  onSave,
  isUploading,
  fallback = "/img/article_placeholder.jpg",
}) => {
  const previewSrc = selectedImage
    ? URL.createObjectURL(selectedImage)
    : (currentImage?.trim() || fallback);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
      <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h4>
      <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">{hint}</p>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr,180px] gap-4 items-start">
        {/* Input + actions */}
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Sélectionner une image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onSelect(e.target.files?.[0] ?? null)}
            className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                       bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                       focus:outline-none focus:ring-2 focus:ring-violet-400"
          />

          <div className="mt-3 flex items-center gap-3">
            <Button
              text="Sauvegarder"
              icon={FaSave}
              onClick={onSave}
              type="button"
              className="!rounded-xl gap-2"
              isLoading={isUploading}
              disabled={!selectedImage}
            />
            {selectedImage && (
              <Button
                text="Annuler"
                icon={FaTimes}
                type="button"
                onClick={() => onSelect(null)}
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
            )}
          </div>
        </div>

        {/* Aperçu */}
        <div className="relative">
          <img
            src={previewSrc}
            alt={`${title} — aperçu`}
            className="w-full h-40 object-cover rounded-xl shadow ring-2 ring-white dark:ring-slate-800"
            onError={(e) => {
              e.currentTarget.src = fallback;
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReglementInterieurForm;
